import { isUnknownRecord } from '../validation/unknown-record';

// Normalizes legacy / intake answer vocabulary to a canonical
// `program_definitions.key`. The existing app / prototype (and migrated data)
// encode the selected program with positional legacy keys (`focus1..focusN`) or
// free-text values; galapagos ingests them by folding onto the canonical
// catalog key here, at the read boundary, rather than trusting a client-side
// substance table (the migration plan's "invert SUBSTANCE_LOOKUP to a server
// read"). A value already canonical passes through unchanged.
//
// Exported so a client test can DERIVE a legacy spelling and the canonical key
// it folds onto rather than hardcoding the pair. ADR-015 Rule 1 forbids a
// substance literal in `apps/mobile/**` and `apps/web/**`, and the fold is
// exactly the kind of server-owned vocabulary that rule exists to keep out of a
// client — a test that names the pair also stops covering any alias added here
// later. Read-only by type: this is the one owner of the mapping.
export const LEGACY_PROGRAM_KEY_ALIASES: Readonly<Record<string, string>> = {
  focus1: 'alcohol',
  focus2: 'tobacco',
  focus3: 'opioid',
  focus4: 'stimulants',
  focus5: 'cannabis',
  pain: 'opioid',
  stimulant: 'stimulants',
  // The tobacco programme is stored as `nicotine` on real check-in rows — see
  // the canonical stored fixture in `check-ins/fixtures.ts`, which mirrors the
  // shape the database actually holds. The catalogue keys its copy under
  // `tobacco`, so without this alias a member's smoke-free history resolves
  // `program_nicotine_*`, finds nothing, and silently renders the neutral
  // fallback instead of their own programme's words.
  nicotine: 'tobacco',
};

/**
 * Coerce a primitive answer to a non-empty trimmed string; non-primitives →
 * undefined. Voice-session prompt variables depend on this leniency: legacy
 * persisted scalar answers must keep resolving to a printable string so an
 * existing member's session can still start. Member-FACING display fields must
 * use `asDisplayString` instead.
 */
export function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

/**
 * Accept only an actual non-empty trimmed string; everything else → undefined.
 * For member-facing display fields (preferred name, free-text motivation): a
 * malformed answer degrades to absent — never renders as `"true"` or `"0"`.
 */
export function asDisplayString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Fold a raw answer value onto its canonical program key. Returns `undefined`
 * for an empty/absent value. Does NOT assert the key exists in the catalog —
 * callers validate against `program_definitions` (an unknown key resolves to no
 * program, which is a valid non-enrollment outcome, not an error).
 */
export function normaliseProgramKey(raw: unknown): string | undefined {
  const key = asNonEmptyString(raw);
  if (key === undefined) return undefined;
  return LEGACY_PROGRAM_KEY_ALIASES[key] ?? key;
}

/**
 * Read an onboarding answer by key, tolerating the shapes the intake flow
 * actually persists: flat (`key`), slash-prefixed (`/key`), `screen`-nested, and the
 * flat `/screen/key` pointer a writer leaves when it skips relativizing the RFC 6901
 * flow-state root (ADR-046). Shared so every consumer of
 * `onboarding_sessions.answers` (voice personalization, enrollment, the care-team
 * intake read) reads the same shapes rather than diverging — a second copy of this
 * rule ends up tolerating a different set, which reads as "not answered" for an
 * answer the member actually gave.
 */
export function readAnswer(answers: Record<string, unknown>, key: string): unknown {
  if (answers[key] !== undefined) return answers[key];
  const slashKey = `/${key}`;
  if (answers[slashKey] !== undefined) return answers[slashKey];
  const pointerKey = `/screen/${key}`;
  if (answers[pointerKey] !== undefined) return answers[pointerKey];
  const screen = answers.screen;
  if (isUnknownRecord(screen)) {
    return screen[key] ?? screen[slashKey];
  }
  return undefined;
}

/**
 * Answer keys that carry the member's selected program, in resolution order.
 *
 * One owner: `resolvePrimaryProgramKey` reads these to pick the primary program, and
 * a display surface reads the same list to know which entries are program codes
 * rather than free text. Two lists drift, and the shorter one silently stops
 * resolving a flow version's spelling.
 *
 * Two readings, both intended. `resolvePrimaryProgramKey` treats the order as
 * PRECEDENCE — first spelling that carries a non-null answer wins, the rest are
 * ignored — because a member has one primary programme. A reader that needs
 * every programme selected (the server-side `selectedProgramKeys`) UNIONS the
 * spellings instead, because stopping at the first would leave a real selection
 * unresolved. The divergence is deliberate; which one is authoritative depends
 * on whether the caller wants the primary or the set.
 */
export const PROGRAM_ANSWER_KEYS = ['program_key', 'program', 'substance'] as const;

/**
 * Clinical priority ranking for the single primary program when a member
 * selects more than one — the program to work first (lower index = higher
 * priority). Server-authored so no consumer hardcodes a key switch; shared so
 * voice personalization and the member-profile view can't drift on the order.
 */
export const PROGRAM_PRIMARY_PRIORITY = [
  'opioid',
  'stimulants',
  'alcohol',
  'cannabis',
  'tobacco',
] as const;

/**
 * The member's single primary program KEY from their onboarding answers — for
 * a multi-select `program_key`/`program`/`substance` answer, the highest
 * clinical-priority selection; a selection outside the ranked substances falls
 * back to the first selection; an empty/absent selection resolves to undefined.
 * Key-level only — label mapping and any further fallbacks stay with callers.
 */
export function resolvePrimaryProgramKey(answers: Record<string, unknown>): string | undefined {
  let raw: unknown;
  for (const key of PROGRAM_ANSWER_KEYS) {
    raw = readAnswer(answers, key);
    // Keep looking past a null, not only past an absent key: a flow version that
    // writes an explicit null for the spelling it does not use must not shadow the
    // spelling that carries the answer.
    if (raw !== undefined && raw !== null) break;
  }
  const selected = (Array.isArray(raw) ? raw : [raw])
    .map((item) => normaliseProgramKey(item))
    .filter((key): key is string => key !== undefined);
  for (const key of PROGRAM_PRIMARY_PRIORITY) {
    if (selected.includes(key)) return key;
  }
  return selected[0];
}

/**
 * The member's preferred name from onboarding, trimmed; undefined when absent,
 * blank, or not a string. Display-strict: both consumers show the value to the
 * member (profile header, voice greeting) and both degrade gracefully — the
 * profile hides the name line, the voice prompt falls back to its neutral
 * default — so a legacy scalar answer must never surface as `"true"`.
 */
export function resolvePreferredName(answers: Record<string, unknown>): string | undefined {
  return asDisplayString(readAnswer(answers, 'preferred_name'));
}
